<template>
  <div class="cwc-portal-menu">
    <el-popover
      v-model="isOpenPopover"
      popper-class="application-night-dot-popover"
      trigger="click"
      :showArrow="false"
      role="dialog"
      :aria-hidden="false"
      aria-label="Applications menu"
    >
      <div class="application-popover-content" @click="handleContentClick">
        <div v-if="isMobile" class="header-content">
          <div class="icon-close" @click="isOpenPopover = false">
            <CwcIcon :width="24" name="ico_x"/>
          </div>
        </div>
        <div v-if="isLoading" class="application-loading">
          <CwcLoading />
        </div>
        <template v-else>
          <div v-if="applications.length > 0" class="application-wrapper">
            <div
              v-for="application in applications"
              :key="application.name"
              class="application-item"
              @click="openApplication(application)"
              >
              <div class="application-item-wrapper" :class="{ 'flex-start':  application.description }">
                <img v-if="application.icon" class="application-icon" :src="application.icon" alt="Application Icon">
                <CwcIcon v-else :width="40" name="ico_paper_2" color="#ff2e38" color2="#fff1ef" isTwoTone/>
                <div class="application-item-info">
                  <div class="application-name">{{ application.name }}</div>
                  <div class="application-description">{{ application.description }}</div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
      <CwcIconButton
        v-show="showPortalButton"
        slot="reference"
        :iconName="JSON.stringify({ name: 'ico_dot_9_grid' })"
        size="medium"
        type="tonal"
        @click="getApplicationList()"
      />
    </el-popover>
    <CwcWarningDialog
      v-model="isError"
      title="ไม่สามารถนำทางได้"
      message="เกิดข้อผิดพลาดในการนำทางไปยังปลายทาง โปรดลองอีกครั้ง"
      :showClose="true"
      :showConfirmButton="true"
      :showCancelButton="false"
      center
      confirmButtonText="ตกลง"
    />
  </div>
</template>

<script>
import axios from 'axios'
import CwcLoading from './CwcLoading.vue'
import CwcIcon from './CwcIcon.vue'
import CwcIconButton from './CwcIconButton.vue'
import jwt from 'jsonwebtoken'

export default {
  components: {
    CwcLoading,
    CwcIcon,
    CwcIconButton,
  },
  props: {
    domainEcoSystem: { type: String, default: 'https://staging-developers.conicle.co' },
    apiPath: { type: String, default: 'rest/api/v1/license/application-setting/' },
    type: { type: String, default: 'portal-menu-user' },
    accessToken: { type: String, default: '' },
    refreshToken: { type: String, default: '' },
    email: { type: String }
  },
  data () {
    return {
      isOpenPopover: false,
      isLoading: false,
      applications: [],
      isMobile: window.innerWidth <= 768,
      isDataCached: false,
      isError: false
    }
  },
  computed: {
    showPortalButton () {
      const hasApplications = this.applications?.length > 0
      return this.accessToken && hasApplications
    }
  },
  mounted () {
    this.getApplicationList()
    window.addEventListener('resize', this.handleResize)
    this.$nextTick(() => {
      const popoverEl = document.querySelector('.application-night-dot-popover')

      if (popoverEl) {
        this.clickHandler = (event) => {
          if (this.isMobile) this.isOpenPopover = false
        }
        popoverEl.addEventListener('click', this.clickHandler)
      }
    })

    // Listen for locale changes from parent application
    this.localeChangeHandler = (event) => {
      const newLocale = event.detail && event.detail.locale
      const i18n = this.$i18n || (window.ConicleWebComponents && window.ConicleWebComponents.i18n)
      if (i18n && newLocale) {
        i18n.locale = newLocale
      }
      if (this.$store && newLocale) {
        this.$store.commit('SET_STATE', { name: 'locale', val: newLocale })
      }
    }
    document.addEventListener('locale-changed', this.localeChangeHandler)
  },
  beforeDestroy () {
    if (this.clickHandler) {
      const popoverEl = document.querySelector('.application-night-dot-popover')
      if (popoverEl) {
        popoverEl.removeEventListener('click', this.clickHandler)
      }
    }
    if (this.localeChangeHandler) {
      document.removeEventListener('locale-changed', this.localeChangeHandler)
    }
    window.removeEventListener('resize', this.handleResize)

    this.isDataCached = false
  },
  methods: {
    async getApplicationList () {
      if (this.isDataCached) return
      this.isLoading = true
      try {
        const slash = this.domainEcoSystem.endsWith('/') ? '' : '/'
        const { data } = await axios.get(`${this.domainEcoSystem}${slash}${this.apiPath}?type=${this.type}`, {
          headers: {
            'Authorization': `Bearer ${this.accessToken}`
          }
        })
        this.applications = data.data.results
        this.isDataCached = true
      } catch (error) {
        console.error(error)
        this.applications = []
      } finally {
        this.isLoading = false
      }
    },

    async openApplication (application) {
      let newWindow = null
      // For mobile, we need to create a new window to avoid popup blocker
      if (this.isMobile) {
        newWindow = window.open('', '_blank')
      }
      const url = await this.generateOpenApplicationUrl(application)
      if (!url) {
        this.isError = true
        return
      }
      this.isOpenPopover = false

      try {
        if (this.isMobile && newWindow) {
          newWindow.location = url
        } else {
          // For desktop, we can use window.open directly
          const win = window.open(url, '_blank')
          if (!win || win.closed || typeof win.closed === 'undefined') {
            window.location.href = url
          }
        }
      } catch (error) {
        console.error('Error opening application:', error)
        window.location.href = url
      }
    },

    async generateOpenApplicationUrl (application) {
      const { login_type: loginType, login_url: loginUrl, url } = application
      switch (loginType) {
        case 'auth_code':
          return this.generateAuthCodeUrl(loginUrl, url)
        case 'auth_code_token_space':
          return this.generateAuthCodeTokenSpaceUrl(loginUrl)
        case 'redirect_token':
          return this.generateRedirectTokenUrl(url)
        default:
          return url
      }
    },

    async generateAuthCodeUrl (loginUrl, url) {
      const authCode = await this.generateAuthCode('login')
      if (!authCode) return null

      const { one_id: oneId } = await this.decodeJwtToken(this.accessToken)

      return `${loginUrl}?auth_code=${authCode}&one_id=${oneId}${url ? `&redirect_url=${url}` : ''}`
    },

    async generateAuthCodeTokenSpaceUrl (loginUrl) {
      const authPayload = {
        access_token: this.accessToken,
        refresh_token: this.refreshToken
      }

      const authCode = await this.generateAuthCode('token_space', authPayload)
      if (!authCode) return null

      return `${loginUrl}?auth_code=${authCode}&lang=${this.$store.state.locale}`
    },

    generateRedirectTokenUrl (url) {
      return `${url}?access_token=${this.accessToken}&refresh_token=${this.refreshToken}&lang=${this.$store.state.locale}`
    },

    async generateAuthCode (actionType = 'login', authPayload) {
      try {
        const form = {
          action_type: actionType,
          session_period: 60,
          ...(actionType === 'token_space' && authPayload ? { auth_payload: authPayload } : {})
        }

        const { data } = await axios.post(`${this.domainEcoSystem}/rest/api/v1/ad/auth-code/`, form, {
          headers: {
            'Authorization': `Bearer ${this.accessToken}`
          },
        })

        return data.data.auth_code
      } catch (error) {
        return null
      }
    },

    async decodeJwtToken (accessToken) {
      if (accessToken) {
        const data = jwt.decode(accessToken)
        return data
      }
      return null
    },

    handleContentClick (event) {
      if (this.isMobile) {
        const popoverContent = event.target.closest('.application-night-dot-popover')
        if (popoverContent) this.isOpenPopover = false
      }
    },

    handleResize () {
      this.isMobile = window.innerWidth <= 768
    }
  }
}
</script>

<style lang="scss">
@import '../design-token/design-system.scss';

.cwc-portal-menu {

  @at-root .application-night-dot-popover {
    border: none !important;
    box-shadow: none !important;
    position: absolute;
    background: none !important;
    min-width: auto !important;
    border-radius: 0px !important;
    border: none !important;
    padding: 0 !important;

    .cwc-icon-button.icon-button-tonal {
      color: #720c12 !important;
    }

    .header-content {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      margin-bottom: 4px;

      .icon-close {
        cursor: pointer;
      }
    }

    .application-popover-content {
      width: 320px;
      background-color: #FFFFFF;
      border: 1px solid #ebecf0;
      border-radius: 24px;
      padding: 16px;
      box-shadow: $shadow-neutral-xl;
      position: absolute;
      top: 12px;
      right: -20px;
      left: unset;
      z-index: 1000;
      display: flex;
      flex-direction: column;
      max-height: calc(100vh - 120px);
      overflow: auto;

        &::-webkit-scrollbar {
          display: none;
        }

        & {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }

      .application-loading {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100%;
      }

      .application-empty {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100%;
        text-align: center;
      }

      .application-wrapper {
        display: flex;
        flex-direction: column;
        gap: $spacing-3xs;

        .application-item {
          .application-item-wrapper {
            align-items: center;
            display: flex;
            gap: 16px;
            padding: $spacing-xs;
            cursor: pointer;

            &.flex-start {
              align-items: flex-start;
            }

            .application-icon {
              width: 40px;
              height: 40px;
              overflow: hidden;
              object-fit: contain;
              border-radius: $radius-sm;
            }

            .application-item-info {
              display: flex;
              flex-direction: column;
              flex: 1;

              .application-name {
                font-size: 16px;
                font-weight: 600;
                color: #3e4044;
                width: 200px;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
              }

              .application-description {
                font-size: 12px;
                color: #63666e;
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
                overflow: hidden;
                text-overflow: ellipsis;
                text-align: start;
              }
            }

            &:hover {
              background-color: #f7f7f8;
              border-radius: $radius-md;
            }
          }
        }
      }
    }
    .popper__arrow {
      display: none !important;
    }

    @media (max-width: $breakpoint-md) {
      content: '';
      position: fixed;
      inset: 0;
      background-color: rgba(0, 0, 0, 0.5) !important;
      z-index: 2000;
      border-radius: 0px !important;
      margin-top: 0px !important;

      &.el-popover {
        top: 0px !important;
        left: 0px !important;
        border-radius: 0px !important;
      }

      .application-popover-content {
        width: calc(100% - 40px);
        height: fit-content;
        max-height: calc(100% - 40px);
        margin: auto 20px !important;
        top: 0px !important;
        left: auto !important;
        right: auto !important;
        bottom: 0px !important;
      }
    }
  }
}
</style>